import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Menu, Button, Typography, Space, Dropdown, Avatar, Tag, Badge, Drawer, Tabs, Empty, Tooltip, Divider } from 'antd';
import {
  DashboardOutlined,
  CalendarOutlined,
  LogoutOutlined,
  UserOutlined,
  SwapOutlined,
  ShopOutlined,
  ClockCircleOutlined,
  SafetyCertificateOutlined,
  CoffeeOutlined,
  ScheduleOutlined,
  WalletOutlined,
  BankOutlined,
  FileTextOutlined,
  CheckSquareOutlined,
  BellOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  RightOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import api, { API_BASE_URL } from '../api';
import ChangePasswordModal from './ChangePasswordModal';
import {
  fetchAllStaffNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead
} from '../services/staffNotificationService';

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;

const checkStaffIsFullScreen = () => {
  return typeof document !== 'undefined' && !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  );
};

const StaffLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(checkStaffIsFullScreen);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(checkStaffIsFullScreen());
    };

    setIsFullscreen(checkStaffIsFullScreen());

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [location.pathname]);

  const toggleFullScreen = () => {
    try {
      if (!isFullscreen) {
        const docEl = document.documentElement;
        if (docEl.requestFullscreen) {
          docEl.requestFullscreen().catch(() => {});
        } else if (docEl.webkitRequestFullscreen) {
          docEl.webkitRequestFullscreen();
        } else if (docEl.mozRequestFullScreen) {
          docEl.mozRequestFullScreen();
        } else if (docEl.msRequestFullscreen) {
          docEl.msRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(() => {});
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
    } catch (_) {}
  };
  const [user, setUser] = useState(null);
  const [brand, setBrand] = useState('Staff Portal');
  const [logoUrl, setLogoUrl] = useState('');
  const [hasSidebarAccess, setHasSidebarAccess] = useState(false);

  // Central Notification State (Zero DB Writes)
  const [notifDrawerOpen, setNotifDrawerOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [activeNotifTab, setActiveNotifTab] = useState('ALL');

  useEffect(() => {
    try {
      const uStr = sessionStorage.getItem('impersonate_user') || localStorage.getItem('user');
      if (uStr) {
        const u = JSON.parse(uStr);
        setUser(u);
      }
    } catch (_) {}

    // Load brand & permissions
    const loadInfo = async () => {
      try {
        const res = await api.get('/admin/user-access/sidebar-brand');
        if (res.data?.brand?.displayName) setBrand(res.data.brand.displayName);
        if (res.data?.info?.logoUrl) {
          const lUrl = res.data.info.logoUrl;
          setLogoUrl(lUrl.startsWith('/') ? `${API_BASE_URL}${lUrl}` : lUrl);
        }
      } catch (_) {}

      try {
        const permRes = await api.get('/admin/user-access/my-sidebar-permissions');
        if (Array.isArray(permRes.data?.permissionKeys) && permRes.data.permissionKeys.length > 0) {
          setHasSidebarAccess(true);
        }
      } catch (_) {}

      try {
        const profRes = await api.get('/me/profile');
        if (profRes.data?.success && profRes.data?.profile) {
          const p = profRes.data.profile;
          setUser((prev) => {
            const merged = {
              ...(prev || {}),
              ...p,
              photoUrl: p.photoUrl || prev?.photoUrl
            };
            try {
              localStorage.setItem('user', JSON.stringify(merged));
            } catch (_) {}
            return merged;
          });
        }
      } catch (_) {}
    };

    loadInfo();

    const handleProfileUpdated = (e) => {
      if (e?.detail) {
        setUser(e.detail);
      } else {
        try {
          const uStr = sessionStorage.getItem('impersonate_user') || localStorage.getItem('user');
          if (uStr) setUser(JSON.parse(uStr));
        } catch (_) {}
      }
    };

    window.addEventListener('profile-updated', handleProfileUpdated);
    window.addEventListener('storage', handleProfileUpdated);

    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdated);
      window.removeEventListener('storage', handleProfileUpdated);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.clear();
    navigate('/');
  };

  const handleSwitchToAdmin = () => {
    sessionStorage.setItem('portal_mode', 'admin');
    localStorage.setItem('portal_mode', 'admin');
    navigate('/dashboard');
  };

  const menuItems = [
    {
      key: '/staff/dashboard',
      icon: <DashboardOutlined style={{ fontSize: 16 }} />,
      label: 'My Dashboard',
    },
    {
      key: '/staff/attendance',
      icon: <CalendarOutlined style={{ fontSize: 16 }} />,
      label: 'Attendance & Calendar',
    },
    {
      key: '/staff/leave',
      icon: <CoffeeOutlined style={{ fontSize: 16 }} />,
      label: 'Leave & Encashment',
    },
    {
      key: '/staff/roster',
      icon: <ScheduleOutlined style={{ fontSize: 16 }} />,
      label: 'Roster & Shifts',
    },
    {
      key: '/staff/salary',
      icon: <WalletOutlined style={{ fontSize: 16 }} />,
      label: 'Salary & Payslip',
    },
    {
      key: '/staff/advances',
      icon: <BankOutlined style={{ fontSize: 16 }} />,
      label: 'Advances',
    },
    {
      key: '/staff/expenses',
      icon: <FileTextOutlined style={{ fontSize: 16 }} />,
      label: 'Expenses',
    },
    {
      key: '/staff/tasks',
      icon: <CheckSquareOutlined style={{ fontSize: 16 }} />,
      label: 'Tasks',
    },
    {
      key: '/staff/sales',
      icon: <ShopOutlined style={{ fontSize: 16 }} />,
      label: 'Sales',
    },
    {
      key: '/staff/profile',
      icon: <UserOutlined style={{ fontSize: 16 }} />,
      label: 'My Profile',
    },
  ];

  const selectedKey = location.pathname.startsWith('/staff/attendance')
    ? '/staff/attendance'
    : location.pathname.startsWith('/staff/leave')
    ? '/staff/leave'
    : location.pathname.startsWith('/staff/roster')
    ? '/staff/roster'
    : location.pathname.startsWith('/staff/salary')
    ? '/staff/salary'
    : location.pathname.startsWith('/staff/advances')
    ? '/staff/advances'
    : location.pathname.startsWith('/staff/expenses')
    ? '/staff/expenses'
    : location.pathname.startsWith('/staff/tasks') || location.pathname.startsWith('/staff/todo')
    ? '/staff/tasks'
    : location.pathname.startsWith('/staff/sales')
    ? '/staff/sales'
    : location.pathname.startsWith('/staff/profile') || location.pathname.startsWith('/staff/my-profile')
    ? '/staff/profile'
    : '/staff/dashboard';

  const getHeaderTitle = () => {
    switch (selectedKey) {
      case '/staff/attendance': return 'My Attendance & Calendar';
      case '/staff/leave': return 'Leave & Encashment';
      case '/staff/roster': return 'Roster & Shifts';
      case '/staff/salary': return 'Salary & Payslip';
      case '/staff/advances': return 'Salary Advances';
      case '/staff/expenses': return 'My Expenses';
      case '/staff/tasks': return 'Tasks & To-Do';
      case '/staff/sales': return 'Sales & Field Orders';
      case '/staff/profile': return 'My Profile';
      default: return 'Staff Dashboard';
    }
  };

  const userPhoto = user?.photoUrl || user?.profilePhotoUrl || user?.avatar;
  const headerPhotoUrl = userPhoto
    ? (String(userPhoto).startsWith('http')
        ? userPhoto
        : `${API_BASE_URL}${String(userPhoto).startsWith('/') ? '' : '/'}${userPhoto}`)
    : null;

  const loadNotifications = useCallback(async () => {
    try {
      setNotifLoading(true);
      const items = await fetchAllStaffNotifications(user?.id || user?.staffId);
      setNotifications(items);
    } catch (_) {
    } finally {
      setNotifLoading(false);
    }
  }, [user?.id, user?.staffId]);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 60000);
    const handleUpdate = () => loadNotifications();
    window.addEventListener('staff-notif-updated', handleUpdate);
    return () => {
      clearInterval(interval);
      window.removeEventListener('staff-notif-updated', handleUpdate);
    };
  }, [loadNotifications]);

  const unreadNotifCount = notifications.filter((n) => !n.isRead).length;

  const filteredNotifications = notifications.filter((n) => {
    if (activeNotifTab === 'ALL') return true;
    return n.category === activeNotifTab;
  });

  const handleItemClick = (notif) => {
    markNotificationAsRead(notif.id, user?.id || user?.staffId);
    setNotifications((prev) => prev.map((n) => (n.id === notif.id ? { ...n, isRead: true } : n)));
    if (notif.actionUrl) {
      setNotifDrawerOpen(false);
      navigate(notif.actionUrl);
    }
  };

  const handleMarkAllRead = () => {
    const allIds = notifications.map((n) => n.id);
    markAllNotificationsAsRead(allIds, user?.id || user?.staffId);
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };

  const formatNotifTime = (date) => {
    if (!date) return '';
    const d = dayjs(date);
    const now = dayjs();
    const diffMins = now.diff(d, 'minute');
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = now.diff(d, 'hour');
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = now.diff(d, 'day');
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.format('DD MMM YYYY');
  };

  const getNotifCategoryMeta = (notif) => {
    switch (notif.category) {
      case 'LEAVE':
        return {
          icon: <CoffeeOutlined />,
          bg: notif.type === 'success' ? '#ECFDF5' : '#FEF2F2',
          color: notif.type === 'success' ? '#059669' : '#DC2626',
          label: 'Leave'
        };
      case 'EXPENSES':
        return {
          icon: <WalletOutlined />,
          bg: notif.type === 'success' ? '#ECFDF5' : '#FEF2F2',
          color: notif.type === 'success' ? '#059669' : '#DC2626',
          label: 'Expense'
        };
      case 'SALARY':
        return {
          icon: <BankOutlined />,
          bg: '#EFF6FF',
          color: '#2563EB',
          label: 'Salary'
        };
      case 'TASKS':
        return {
          icon: <CheckSquareOutlined />,
          bg: '#FFFBEB',
          color: '#D97706',
          label: 'Task'
        };
      case 'SALES':
        return {
          icon: <ShopOutlined />,
          bg: '#F5F3FF',
          color: '#7C3AED',
          label: 'Sales'
        };
      case 'ROSTER':
      default:
        return {
          icon: <ScheduleOutlined />,
          bg: '#F0FDF4',
          color: '#16A34A',
          label: 'Roster'
        };
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Sider for Staff */}
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        theme="light"
        width={240}
        style={{
          boxShadow: '2px 0 8px 0 rgba(29,35,41,.05)',
          borderRight: '1px solid #e2e8f0',
          position: 'sticky',
          top: 0,
          height: '100vh',
          zIndex: 100,
        }}
      >
        <div style={{
          padding: '18px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          borderBottom: '1px solid #f1f5f9'
        }}>
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" style={{ height: 32, maxWidth: 36, objectFit: 'contain' }} />
          ) : (
            <div style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 700,
              fontSize: 16
            }}>
              S
            </div>
          )}
          {!collapsed && (
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                {brand}
              </div>
              <Tag color="green" style={{ fontSize: 10, lineHeight: '16px', padding: '0 6px', margin: 0 }}>
                Staff Self-Service
              </Tag>
            </div>
          )}
        </div>

        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ borderRight: 0, marginTop: 12, fontWeight: 500 }}
        />

        {/* Portal Switcher in Sidebar if user has sidebar access */}
        {hasSidebarAccess && !collapsed && (
          <div style={{ padding: '16px 12px', marginTop: 'auto', borderTop: '1px solid #f1f5f9' }}>
            <Button
              type="primary"
              ghost
              icon={<ShopOutlined />}
              onClick={handleSwitchToAdmin}
              style={{
                width: '100%',
                borderRadius: 8,
                fontSize: 12,
                height: 36,
                borderColor: '#2563eb',
                color: '#2563eb'
              }}
            >
              Management Portal
            </Button>
          </div>
        )}
              {/* Fullscreen Button */}
        <div style={{
          padding: collapsed ? '8px 8px 12px 8px' : '8px 12px 12px 12px',
          marginTop: hasSidebarAccess ? 0 : 'auto',
          borderTop: hasSidebarAccess ? 'none' : '1px solid #f1f5f9'
        }}>
          <Tooltip title={collapsed ? (isFullscreen ? 'Exit full screen' : 'Full screen') : ''} placement="right">
            <Button
              type="default"
              onClick={toggleFullScreen}
              icon={isFullscreen ? <FullscreenExitOutlined style={{ fontSize: 15 }} /> : <FullscreenOutlined style={{ fontSize: 15 }} />}
              style={{
                width: '100%',
                height: 38,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                fontWeight: 600,
                fontSize: 12,
                color: '#1677ff',
                borderColor: '#d6e4ff',
                backgroundColor: '#f0f5ff'
              }}
            >
              {!collapsed && (isFullscreen ? 'Exit full screen' : 'Full screen')}
            </Button>
          </Tooltip>
        </div>
      </Sider>

      <Layout>
        {/* Top Navbar */}
        <Header style={{
          background: '#fff',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
          height: 64,
          position: 'sticky',
          top: 0,
          zIndex: 99
        }}>
          <Space align="center" size={16}>
            <Title level={4} style={{ margin: 0, color: '#0f172a', fontWeight: 600 }}>
              {getHeaderTitle()}
            </Title>
          </Space>

          <Space align="center" size={16}>
            {hasSidebarAccess && (
              <Button
                type="dashed"
                icon={<SwapOutlined />}
                onClick={handleSwitchToAdmin}
                style={{
                  borderRadius: 8,
                  borderColor: '#2563eb',
                  color: '#2563eb',
                  fontWeight: 500
                }}
              >
                Switch to Management Portal
              </Button>
            )}

            {/* Central Notification Bell */}
            <Badge count={unreadNotifCount} overflowCount={99} offset={[-2, 6]}>
              <Button
                type="text"
                shape="circle"
                icon={<BellOutlined style={{ fontSize: 18, color: notifDrawerOpen ? '#2563eb' : '#334155' }} />}
                onClick={() => setNotifDrawerOpen(true)}
                style={{
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: notifDrawerOpen ? '#eff6ff' : '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  cursor: 'pointer'
                }}
                title="Notifications"
              />
            </Badge>

            <div
              onClick={() => navigate('/staff/profile')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: 8,
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              title="View / Edit My Profile"
            >
              <Avatar
                size={36}
                style={{ background: '#10b981', flexShrink: 0 }}
                src={headerPhotoUrl}
                icon={!headerPhotoUrl && <UserOutlined />}
              />
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                <Text strong style={{ fontSize: 13, color: '#1e293b' }}>
                  {user?.name || 'Staff User'}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {user?.staffId ? `ID: ${user.staffId}` : (user?.phone ? `+91 ${user.phone}` : 'Staff')}
                </Text>
              </div>
            </div>

            <Button
              type="text"
              icon={<KeyOutlined style={{ fontSize: 16, color: '#475569' }} />}
              onClick={() => setChangePasswordOpen(true)}
              title="Change Password"
              style={{
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
            <Button
              type="text"
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              title="Logout"
            />
          </Space>
        </Header>

        {/* Content Body */}
        <Content style={{ padding: '24px', maxWidth: 1400, width: '100%', margin: '0 auto' }}>
          {children}
        </Content>
      </Layout>

      {/* Central Notification Drawer (Zero DB Writes) */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <BellOutlined style={{ fontSize: 18, color: '#2563eb' }} />
              <span style={{ fontWeight: 700, fontSize: 16 }}>Notifications</span>
              {unreadNotifCount > 0 && (
                <Tag color="red" style={{ borderRadius: 10, fontSize: 11, fontWeight: 700, margin: 0 }}>
                  {unreadNotifCount} New
                </Tag>
              )}
            </div>
            <Space size="small">
              {unreadNotifCount > 0 && (
                <Button
                  size="small"
                  type="link"
                  onClick={handleMarkAllRead}
                  style={{ fontSize: 12, padding: 0 }}
                >
                  Mark all as read
                </Button>
              )}
              <Button
                size="small"
                type="text"
                icon={<SyncOutlined spin={notifLoading} />}
                onClick={loadNotifications}
                title="Refresh notifications"
              />
            </Space>
          </div>
        }
        placement="right"
        width={420}
        onClose={() => setNotifDrawerOpen(false)}
        open={notifDrawerOpen}
        bodyStyle={{ padding: '12px 16px', background: '#F8FAFC' }}
        headerStyle={{ borderBottom: '1px solid #E2E8F0', padding: '16px 20px' }}
      >
        <Tabs
          activeKey={activeNotifTab}
          onChange={setActiveNotifTab}
          size="small"
          items={[
            { key: 'ALL', label: `All (${notifications.length})` },
            { key: 'LEAVE', label: `Leaves (${notifications.filter(n => n.category === 'LEAVE').length})` },
            { key: 'EXPENSES', label: `Expenses (${notifications.filter(n => n.category === 'EXPENSES').length})` },
            { key: 'ROSTER', label: `Roster (${notifications.filter(n => n.category === 'ROSTER').length})` },
            { key: 'SALARY', label: `Salary (${notifications.filter(n => n.category === 'SALARY').length})` },
            { key: 'TASKS', label: `Tasks (${notifications.filter(n => n.category === 'TASKS').length})` },
            { key: 'SALES', label: `Sales (${notifications.filter(n => n.category === 'SALES').length})` },
          ]}
        />

        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredNotifications.length === 0 ? (
            <div style={{ padding: '60px 0', textAlign: 'center' }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ color: '#64748B', fontSize: 13 }}>
                    {notifLoading ? 'Checking for latest notifications...' : 'No notifications in this category'}
                  </span>
                }
              />
            </div>
          ) : (
            filteredNotifications.map((n) => {
              const meta = getNotifCategoryMeta(n);
              return (
                <div
                  key={n.id}
                  onClick={() => handleItemClick(n)}
                  style={{
                    background: n.isRead ? '#FFFFFF' : '#F0F9FF',
                    border: n.isRead ? '1px solid #E2E8F0' : '1px solid #BAE6FD',
                    borderRadius: 12,
                    padding: '12px 14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    boxShadow: n.isRead ? '0 1px 2px rgba(0,0,0,0.02)' : '0 2px 4px rgba(14, 165, 233, 0.08)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#38BDF8';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = n.isRead ? '#E2E8F0' : '#BAE6FD';
                    e.currentTarget.style.transform = 'none';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: meta.bg,
                        color: meta.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                        flexShrink: 0
                      }}
                    >
                      {meta.icon}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontWeight: n.isRead ? 600 : 700, fontSize: 13, color: '#0F172A' }}>
                            {n.title}
                          </span>
                          {!n.isRead && (
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#0284C7', display: 'inline-block' }} />
                          )}
                        </div>
                        <span style={{ fontSize: 11, color: '#94A3B8', whiteSpace: 'nowrap' }}>
                          {formatNotifTime(n.timestamp)}
                        </span>
                      </div>

                      <div style={{ fontSize: 12, color: '#475569', marginTop: 4, lineHeight: 1.4 }}>
                        {n.message}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                        <Tag style={{ fontSize: 10, lineHeight: '18px', padding: '0 6px', margin: 0, border: 'none', background: meta.bg, color: meta.color, fontWeight: 600 }}>
                          {meta.label}
                        </Tag>
                        <span style={{ fontSize: 11, color: '#2563EB', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                          {n.actionText} <RightOutlined style={{ fontSize: 9 }} />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Drawer>
      <ChangePasswordModal open={changePasswordOpen} onCancel={() => setChangePasswordOpen(false)} />
    </Layout>
  );
};

export default StaffLayout;
